<template>
  <v-card>
    <v-card-title class="d-flex">
      <v-icon start color="grey">mdi-filter</v-icon> Filter
      <v-icon @click="save" class="ml-auto" end color="grey">mdi-close</v-icon>
    </v-card-title>
    <v-card-text class="px-3 py-8">
      <v-form>
        <sorter
          v-model="sorts"
          :sortableFields="sortableFields"
          :SortTypes="SortTypes"
        />
      </v-form>
      <v-form>
        <v-text-field
          v-model="fulltextSearch"
          variant="outlined"
          hide-details
          placeholder="Freitextsuche"
          class="mb-4"
          clearable
          @click:clear="fulltextSearch = ''"
        />
        <v-select
          variant="plain"
          messages=""
          density="compact"
          class="mt-n3"
          :items="allSearchInItems"
          multiple
          item-title="text"
          item-value="value"
          v-model="filters.Fulltext$Fulltext.fields"
          clearable
          @click:clear="filters.Fulltext$Fulltext.fields = []"
        >
          <template #selection="{ item }">
            <v-chip variant="outlined" class="black--color" color="grey" size="x-small">
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
        </v-select>
        <v-select
          v-model="filters.Status$In"
          variant="outlined"
          placeholder="Status filtern"
          label="Status"
          :items="statusTypes"
          multiple
          item-title="text"
          item-value="value"
          clearable
          @click:clear="filters.Status$In = []"
        >
          <template #chip="{ item }">
            <v-chip size="small">
              <v-avatar :color="item.raw.color" variant="flat" :size="12" class="mr-1">
                <v-icon :icon="item.raw.icon" />
              </v-avatar>
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
        </v-select>

        <v-select
          label="Picklistenart"
          variant="outlined"
          :items="picklistTypes"
          multiple
          item-title="text"
          item-value="value"
          v-model="filters.Art$In"
          clearable
          @click:clear="filters.Art$In = []"
        >
          <template #selection="{ item }">
            <v-chip theme="dark" size="small">
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
        </v-select>

        <date-picker
          v-model="filters.Kmsdatum$From"
          label="Datum von"
          @click:append="filters.Kmsdatum$From = undefined"
        />
        <date-picker
          v-model="filters.Kmsdatum$To"
          label="Datum bis"
          @click:append="filters.Kmsdatum$To = undefined"
        />
         <v-text-field
          v-model="Bediener"
          :rules="[ruleUserShort]"
          variant="outlined"
          hide-details
          label="Bediener"
          maxlength="5"
          clearable
        />

      </v-form>
    </v-card-text>
    <v-divider></v-divider>

    <v-card-actions class="text-center">
      <v-btn color="grey" class="flex-1" variant="outlined" size="large" @click="reset">Zurücksetzen</v-btn>
      <v-btn class="flex-1 btn-navigator-primary" size="large" @click="save">Speichern</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {FulltextSearchField, ValueType} from '@/api/meta/filters';
import {ApiPicklistState, ApiPicklistType} from '@/model/Picklist';
import {computed, ref} from 'vue';
import DatePicker from '../DatePicker.vue';
import Sorter from '../Sorter.vue';
import {useFilterSort} from '@/composables/filterSort';
import usePicklistsStore from '@/store/picklists';

const store = usePicklistsStore();
const emits = defineEmits(['close']);
const {save, reset, filters, sorts, SortTypes} = useFilterSort(store, emits);

const allSearchInItems = ref<Array<{ text: string, value: FulltextSearchField }>>([
  {
    text: 'Tour-Nr.',
    value: {
      path: ['Tour', 'Tournr'],
      valueType: ValueType.Naked,
    },
  },
  {
    text: 'Picklisten-Nr.',
    value: {
      path: ['Picklistnr'],
      valueType: ValueType.Naked,
    },
  },
  {
    text: 'Bediener',
    value: {
      path: ['Kmsbediener'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'KFZ-Kennzeichen',
    value: {
      path: ['Tour/KfzKennzeichen'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Auftrag-Nr.',
    value: {
      path: ['Auftraege', 'Belegnr'],
      valueType: ValueType.Naked,
    },
  },
  {
    text: 'Beleg-Nr. (intern)',
    value: {
      path: ['InterneAuftraege', 'Belegnr'],
      valueType: ValueType.Naked,
    },
  },
  {
    text: 'Kunde',
    value: {
      path: ['Auftraege', 'Name'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Niederlassung / Lager',
    value: {
      path: ['InterneAuftraege', 'Name'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Kennzeichen',
    value: {
      path: ['Tour', 'KfzKennzeichen'],
      valueType: ValueType.StringContains,
    },
  },
]);

const statusTypes = ref([
  {
    text: 'offen',
    value: ApiPicklistState.Todo,
    color: 'primary',
  },
  {
    text: 'in Bearbeitung',
    value: ApiPicklistState.Active,
    color: 'primary-lighten-1',
    icon: 'lock',
  },
  {
    text: 'erledigt',
    value: ApiPicklistState.Done,
    color: 'success',
    icon: 'check-circle',
  },
  {
    text: 'geparkt',
    value: ApiPicklistState.Parked,
    color: 'orange-accent-4',
    icon: 'pause-circle',
  },
]);
const picklistTypes = ref([
  {
    text: 'Aufträge',
    value: ApiPicklistType.Order,
  },
  {
    text: 'Interne Aufträge',
    value: ApiPicklistType.Internal,
  },
  {
    text: 'Touren',
    value: ApiPicklistType.Tour,
  },
]);
const sortableFields = ref([
  {
    text: 'Picklisten Nr.',
    value: 'Picklistnr',
  },
  {
    text: 'Datum',
    value: 'Kmsdatum',
  },
  {
    text: 'Priorität',
    value: 'Kmsprioritaet',
  },
]);

const fulltextSearch = computed({
  set(sWord: string) {
    const words = (sWord ?? '').split(' ').filter((w) => !!w);
    filters.value.Fulltext$Fulltext.words = words;
  },
  get(): string {
    const words = filters.value.Fulltext$Fulltext.words;

    return words.join(' ');
  },
});
const Bediener = computed({
  get() {
    return filters.value.Kmsbediener;
  },
  set(val: string | null | undefined) {
    if (!val) filters.value.Kmsbediener = undefined;
    else filters.value.Kmsbediener = val.toUpperCase();
  },
});

function ruleUserShort(val: string | undefined | null): string|true {
  if (!val) return true;

  if (val.length > 5) return 'zu lang';

  return true;
}

defineExpose({
  ruleUserShort,
  Sorter, DatePicker,
  store,
  save, reset, filters, sorts, SortTypes,
  allSearchInItems,
  statusTypes,
  picklistTypes,
  sortableFields,
  fulltextSearch,
  Bediener,
});

</script>
<style scoped>
.fixed-bottom {
  position: fixed;
  bottom: 0;
  width: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
.v-card__actions {
  justify-content: center;
}
</style>
